import {
  createSelector,
  createSlice,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit'
import { detectCreditCard } from '@vend/credit-card-detector'
import { ApiCustomer, ApiProduct, ApiTax } from '@vend/types'
import { isEqual } from 'lodash'
import moment from 'moment'
import { add, vn } from 'vend-number'
import { SaleAdjustment } from '../../../../types/adjustments'
import { SALE_VERSION } from '../../../components/sale/sale-model.constant'
import { isGiftCard } from '../../../functions/lineItem'
import {
  determineDisplayStatusRedux,
  generateSaleLoyaltyClaimCode,
  getOutstandingBalance,
  getPromotionSummary,
  hasLineItemFulfilment,
  isCompleted,
  isConfirmed,
  isReturn,
  isUnfulfilled,
  saleIsExchange,
} from '../../../functions/sale'
import {
  createUnknownLineItem,
  removeUnknownLineItemByLocalId,
} from '../../../functions/unknownLineItem'
import { getSaleDiscountIsPercentage } from '../../../managers/wr-session-storage'
import { CustomFieldValue } from '../../../models/custom-fields'
import {
  FulfilmentDetail,
  FulfilmentType,
  isDefinedFulfilmentType,
  isNoteTooLong,
} from '../../../models/fulfilment'
import {
  LineItem,
  LineItemTaxComponent,
  UnknownLineItem,
} from '../../../models/line-item'
import { Payment } from '../../../models/payment'
import { CalculateTaxErrorType } from '../../../queries/calculate-tax'
import {
  CustomerBalances,
  DEFAULT_FINALS,
  DEFAULT_TOTALS,
  mergeTotals,
  EcomCustomCharges,
  Finals,
  OriginalSurcharge,
  PayAccountBalance,
  ReceiptNumberDetails,
  Sale,
  SalePicklist,
  SURCHARGE_ADJUSTMENT,
  SurchargeTenderProperties,
  TIP_ADJUSTMENT,
  Totals,
} from '../../../models/sale'
import { createSaleDiscount, SaleDiscount } from '../../../models/sale-discount'
import {
  FULFILMENT_SALE_ATTRIBUTES,
  RegisterSaleAttribute,
  SaleAttributes,
  SaleMetadata,
  SaleStatus,
  SaleSyncStatus,
  ServiceActions,
  ServiceFields,
  User,
} from '../../../models/sale.constants'
import { RootState } from '../../store'
import { lineItemsAdapter, ReduxLineItem } from './lineItemEntityAdapter'

export type FulfilmentOutletNamesById = Record<string, string>

export interface ReduxCurrentSale {
  /** Contains all adjustments from a saved sale and any adjustments made when
   * the sale is active
   */
  adjustments: SaleAdjustment[]
  /**
   * Custom charges from Ecom
   */
  ecomCustomCharges: EcomCustomCharges | null
  /*
   * We need to track if the sale has ecomCustomCharges,
   * In a refund, the user might remove all charges and we need to know if there were any already or not.
   */
  hasEcomCustomCharges: boolean
  /** The change on the sale. */
  change?: Payment | null
  /**
   * Set when the sale is completed by a user on the sale confirmation screen. This has been added to help debug
   * sales incorrectly posted the api.
   */
  completedByUser?: { source: string }
  /** Customer added to the sale */
  customer: ApiCustomer | null
  /**
   * The sale discount information
   */
  discount?: SaleDiscount | null
  /**
   * The customer's balances
   * This excludes any loyalty/store credit earned/redeemed via this sale and
   * its payments
   */
  customerBalances: CustomerBalances | null
  /**
   * Custom fields added to the sale
   */
  customFields: CustomFieldValue[]
  /** Interpreted as either a $ or a % depending on the discountIsPercentage property. */
  displayDiscount: number | string
  displayStatus?: SaleStatus
  /** Whether the sale discount is a percentage or a discreet value. */
  discountIsPercentage: boolean
  /** Stores the final calculations of a sale */
  finals: Finals
  /** Specifies a selected outlet to create the picklist in */
  fulfillmentOutletID: string | null
  /** Whether the custom fields have been updated on the confirmation screen. */
  hasUpdatedCustomFieldsOnConfirmation?: boolean
  /** Whether the note has been updated on the confirmation screen. */
  hasUpdatedNoteOnConfirmation?: boolean
  /** Whether a customer has been added to the sale on the confirmation screen. */
  hasAddedCustomerOnConfirmation?: boolean
  /** Flag to display the add customer to sale tooltip in payment screen */
  hideAttachCustomerTooltip: boolean
  hydratedFromGraphQL: boolean
  /** The unique ID of this Sale generated on the server. */
  id?: string
  /** The server-assigned ID of the quote when the sale is a quote. */
  quoteId?: string
  /** For continued non-tax sales. If it is a continued sale with no tax? */
  isSavedNonTax?: boolean
  /** For saved services. If it is a registered saved service? */
  isSavedService?: boolean
  /** For unfulfilled sales. If it is a registered unfulfilled sale? */
  isSavedUnfulfilled?: boolean
  /** For retrieved parked sales. True when the sale was loaded from the backend with PARKED status. */
  isSavedParked?: boolean
  /** The unique ID of this Sale locally generated on the client. */
  localId: string
  /** Normalised line items of the sale. */
  lineItems: ReduxLineItem
  /**
   * The unique ID to identify the same Sale for CreateSaleMutation when applying multi retries.
   *
   * NOTE: This property is never set on an active Sale and hence never populated in Redux. However it is still
   * represented here for consistency with the Sale model.
   */
  mutationID?: string
  /** Sale note. */
  note: string
  /** If the Sale is a return, the payments for the original sale. */
  originalSalePayments: Payment[]
  /** If the Sale is a return and the original sale had a surcharge, the surcharge details. */
  originalSurcharge: OriginalSurcharge | null
  /** The remaining balance for this sale. */
  // TODO: Rename this to `initialOutstandingBalance` after the migration to Redux is complete.
  paidBalance: number | null
  /** Sale payments. */
  payments: Payment[]
  /** Stores the account balance payment recorded for a customer */
  payAccountBalance: PayAccountBalance | null
  /** The promo code added to the sale. */
  promoCode: string
  /** The api response status when try to apply a promo code. */
  promoCodeStatus: string | null
  /** The promo code promotion id. */
  promoCodePromotionId: string | null
  /** The ID of a promotion that applies to this Sale and has been suggested to the cashier. */
  potentialPromotionId?: string | null
  /** Identify that the sale can be completed. Assigned when the sale is transitioned to either layby or onaccount. */
  readyToComplete: boolean
  isSubmittedSaleEarly: boolean
  /** The email address where to send the sale receipt. */
  receiptEmailAddress: string
  /** The id of the selected receipt to email. */
  receiptID: string
  /** The receipt number details. */
  receiptNumberDetails?: ReceiptNumberDetails | string
  /** Confirmed sale discount percentage for returns. */
  returnDiscountPercentage: number | null
  /** If this Sale is a return, the ID of the original sale this sale is a return for. */
  returnFor: string | null
  /** The ID of the register the Sale was made on. */
  registerId?: string | null
  /** Information regarding the user operating the register */
  user?: User | null
  /** For cash discounting; ensures that a service fee is not applied to a sale if specifically requested by a user. */
  userRemovedSaleServiceFee: boolean
  /**
   * LSPay cash discount percentage configured on the sale, as a whole number
   * (e.g. `4` for 4%). `0` (or absent) means no LSPay discount.
   */
  lspCashDiscountPercentage?: number
  /** Date the sale was made. */
  saleDate?: string | null
  /** Stores data related to a service */
  serviceFields: ServiceFields
  /** The short code used for loyalty. */
  shortCode?: string
  /** Source of the sale e.g. quote. */
  source?: string
  /** Relevant ID to the source of sale e.g. quote ID. */
  sourceId?: string
  /** Status of a sale */
  status?: SaleStatus
  /** The date and time the sale status was last updated */
  statusUpdatedAt?: string | null
  /** Totals of a sale */
  totals: Totals
  /** Sync status of sale with backend */
  syncStatus: SaleSyncStatus
  /** Number of attempts to sync to backend */
  syncAttempts: number
  /** For unfulfilled sales; is the sale in a pickable state? */
  pickable?: boolean
  /** Unknown products added to the sale. */
  unknownLineItems: UnknownLineItem[]
  /** The sale model version */
  version: number
  /** The details of the fulfilment of the sale */
  fulfilmentDetails: FulfilmentDetail[]
  /** Outlet names needed to render CLF grouping on the payment summary. */
  fulfilmentOutletNamesById: FulfilmentOutletNamesById
  /** Sale attributes */
  attributes: string[]

  metadata?: SaleMetadata[]

  /** Sale associated picklists */
  picklists?: SalePicklist[]

  /** Auto-tax calculation state */
  autoTaxCalculating: boolean

  /** Last calculated auto-tax input key to prevent duplicate API calls */
  autoTaxLastCalculatedKey: string

  /** Store original finals before auto-tax replaces them, so we can restore when fulfillment type changes */
  preAutoTaxFinals: Finals | null

  /** Store original tax info per line item before auto-tax replaces it */
  preAutoTaxLineItems: Record<
    string,
    {
      tax: string
      baseTax: string
      taxInfo?: ApiTax
      taxComponents: LineItemTaxComponent[]
    }
  >

  /** Error from the tax calculation API (e.g. address mismatch, inactive category, or unspecified failure) */
  calculateTaxError?: CalculateTaxErrorType

  /** Line item IDs that have inactive/outdated tax categories */
  autoTaxInactiveCategoryLineItemIds?: string[]

  /** Flag to track if payment requests were auto-cancelled */
  paymentRequestsAutoCancelled: boolean
}

type UpdateSaleCalculableFieldsPayload = {
  lineItems: Array<
    Pick<
      LineItem,
      'localId' | 'totals' | 'taxComponents' | 'taxInfo' | 'customerTaxApplied'
    >
  >
  discount: SaleDiscount | null
  totals: Totals
  finals: Finals
  displayStatus?: SaleStatus
}

type UpdateLineItemTaxPayload = {
  /** Line item local id because that's what line item adapter used for id */
  lineItemLocalID: string
  adjusted: boolean
  customerTaxApplied: boolean
  taxInfo?: ApiTax | undefined
}

export const initialState: ReduxCurrentSale = {
  version: SALE_VERSION,
  customer: null,
  discountIsPercentage: true,
  displayDiscount: '0',
  hideAttachCustomerTooltip: false,
  hydratedFromGraphQL: false,
  localId: '',
  note: '',
  originalSalePayments: [],
  originalSurcharge: null,
  paidBalance: null,
  payments: [],
  promoCode: '',
  promoCodeStatus: null,
  promoCodePromotionId: null,
  potentialPromotionId: undefined,
  fulfillmentOutletID: null,
  readyToComplete: false,
  isSubmittedSaleEarly: false,
  receiptEmailAddress: '',
  receiptID: '',
  returnDiscountPercentage: null,
  returnFor: null,
  syncStatus: SaleSyncStatus.UNMODIFIED,
  syncAttempts: 0,
  unknownLineItems: [],
  userRemovedSaleServiceFee: false,
  lspCashDiscountPercentage: 0,
  payAccountBalance: null,
  adjustments: [],
  ecomCustomCharges: {
    total: 0,
    totalIncl: 0,
    totalTax: 0,
    charges: [],
  },
  hasEcomCustomCharges: false,
  serviceFields: {},
  customerBalances: null,
  totals: DEFAULT_TOTALS,
  finals: DEFAULT_FINALS,
  customFields: [],
  fulfilmentDetails: [],
  fulfilmentOutletNamesById: {},
  receiptNumberDetails: undefined,
  hasAddedCustomerOnConfirmation: false,
  hasUpdatedNoteOnConfirmation: false,
  lineItems: lineItemsAdapter.getInitialState(),
  attributes: [],
  id: undefined,
  quoteId: undefined,
  autoTaxCalculating: false,
  autoTaxLastCalculatedKey: '',
  preAutoTaxFinals: null,
  preAutoTaxLineItems: {},
  calculateTaxError: undefined,
  autoTaxInactiveCategoryLineItemIds: undefined,
  paymentRequestsAutoCancelled: false,
}

const currentSaleSlice = createSlice({
  name: 'currentSale',
  initialState,
  reducers: {
    /**
     * Create a new empty current sale state with the given localId.
     */
    newCurrentSale(_, action: PayloadAction<string>): ReduxCurrentSale {
      return {
        ...initialState,
        discountIsPercentage: getSaleDiscountIsPercentage(),
        localId: action.payload,
      }
    },
    /**
     * Populate the currentSale state based on the given Sale e.g. when loading the Sale from IndexedDB or
     * continuing a parked Sale from Sales History.
     */
    setCurrentSale(_, { payload }: PayloadAction<Sale>): ReduxCurrentSale {
      const currentSale: ReduxCurrentSale = {
        ...initialState,
        change: payload.change,
        completedByUser: payload.completedByUser,
        discount: payload.discount
          ? createSaleDiscount(payload.discount)
          : null,
        customer: payload.customer ? { ...payload.customer } : null,
        discountIsPercentage:
          payload.discountIsPercentage ?? getSaleDiscountIsPercentage(),
        displayDiscount: payload.displayDiscount,
        displayStatus: payload.displayStatus,
        hydratedFromGraphQL: payload.hydratedFromGraphQL,
        id: payload.id,
        isSavedNonTax: payload.isSavedNonTax,
        isSavedService: payload.isSavedService,
        isSavedUnfulfilled: payload.isSavedUnfulfilled,
        isSavedParked: payload.isSavedParked,
        localId: payload.localId,
        mutationID: payload.mutationID,
        note: payload.note,
        originalSalePayments: payload.originalSalePayments
          ? [...payload.originalSalePayments]
          : [],
        originalSurcharge: payload.originalSurcharge,
        paidBalance: payload.paidBalance,
        promoCode: payload.promoCode,
        promoCodeStatus: payload.promoCodeStatus,
        promoCodePromotionId: payload.promoCodePromotionId,
        potentialPromotionId: payload.potentialPromotionId,
        fulfillmentOutletID: payload.fulfillmentOutletID,
        readyToComplete: payload.readyToComplete,
        isSubmittedSaleEarly: payload.isSubmittedSaleEarly,
        receiptEmailAddress: payload.receiptEmailAddress,
        receiptID: payload.receiptID,
        receiptNumberDetails: payload.receiptNumberDetails
          ? typeof payload.receiptNumberDetails === 'string'
            ? payload.receiptNumberDetails
            : { ...payload.receiptNumberDetails }
          : undefined,
        returnDiscountPercentage: payload.returnDiscountPercentage,
        returnFor: payload.returnFor,
        registerId: payload.registerId,
        user: payload.user ? { ...payload.user } : undefined,
        saleDate: payload.saleDate,
        shortCode: payload.shortCode,
        source: payload.source,
        sourceId: payload.sourceId,
        syncStatus: payload.syncStatus,
        syncAttempts: payload.syncAttempts,
        pickable: payload.pickable,
        // Any collections or objects that are not simple fields need to be copied. Otherwise Redux marks the original
        // objects as non extensible and, if the Redux migration feature flag is off, we can't add new items to them.
        // See #im-sev0-burly-meerkat https://lightspeedhq.slack.com/archives/C05TTUER6NT
        payments: [...payload.payments],
        unknownLineItems: [...payload.unknownLineItems],
        userRemovedSaleServiceFee: payload.userRemovedSaleServiceFee || false,
        lspCashDiscountPercentage: payload.lspCashDiscountPercentage || 0,
        payAccountBalance: payload.payAccountBalance
          ? Object.assign({}, payload.payAccountBalance)
          : null,
        adjustments: payload.adjustments ? [...payload.adjustments] : [],
        ecomCustomCharges: payload.ecomCustomCharges
          ? { ...payload.ecomCustomCharges }
          : null,
        hasEcomCustomCharges: payload.hasEcomCustomCharges || false,
        serviceFields: payload.serviceFields
          ? { ...payload.serviceFields }
          : {},
        customerBalances: payload.customerBalances
          ? { ...payload.customerBalances }
          : null,
        totals: mergeTotals(payload.totals),
        finals: { ...DEFAULT_FINALS, ...payload.finals },
        hideAttachCustomerTooltip: payload.hideAttachCustomerTooltip
          ? payload.hideAttachCustomerTooltip
          : false,
        // in redux state, customFields array and all its customField items are immutable
        // in the sale object, its customFields array mutable, but all its customField items are immutable
        // this is because the copy customFields below is copying by reference
        customFields: payload.customFields ? [...payload.customFields] : [],
        fulfilmentDetails: payload.fulfilmentDetails
          ? [...payload.fulfilmentDetails]
          : [],
        attributes: [...payload.attributes],
        picklists: payload.picklists ? [...payload.picklists] : undefined,
        preAutoTaxFinals: payload.preAutoTaxFinals || null,
        preAutoTaxLineItems: payload.preAutoTaxLineItems || {},
      }

      if (payload.status) {
        // FIXME: the OG sales model is set as string, not great, once the type is changed, we should remove this cast
        currentSale.status = payload.status as SaleStatus
        currentSale.statusUpdatedAt = payload.saleDate
      }

      if (payload.lineItems) {
        currentSale.lineItems = lineItemsAdapter.addMany(
          initialState.lineItems,
          payload.lineItems
        )
      }

      return currentSale
    },
    /**
     * Set the sale note.
     */
    setSaleNote(state, action: PayloadAction<string>) {
      state.note = action.payload
    },
    /**
     * toggle the hasUpdatedNoteOnConfirmation state. This is used to determine if the note has been updated on the confirmation screen.
     */
    setHasUpdateNoteOnConfirmation(state, action: PayloadAction<boolean>) {
      state.hasUpdatedNoteOnConfirmation = action.payload
    },
    /**
     * Set the outlet ID for fulfillment.
     */
    setFulfillmentOutletID(state, action: PayloadAction<string>) {
      return {
        ...state,
        fulfillmentOutletID: action.payload,
      }
    },
    /**
     * Add a payment to the sale.
     */
    addPayment(state, action: PayloadAction<Payment>) {
      state.payments.push(action.payload)
    },
    /**
     * Add a promo code to the sale. when param code equals to '' will remove any promo code from sale. Borrowed from addOrRemovePromoCode in sale manager
     */
    addOrRemovePromoCode(state, action: PayloadAction<string>) {
      state.promoCode = action.payload
    },
    /**
     * Add an unknown line item to the sale based on the given search query (product name / SKU).
     */
    addUnknownLineItem(state, action: PayloadAction<string>) {
      state.unknownLineItems.push(createUnknownLineItem(action.payload))
    },
    /**
     * Remove the an unknown line item with the given localId from the sale.
     */
    removeUnknownLineItem(state, action: PayloadAction<string>) {
      state.unknownLineItems = removeUnknownLineItemByLocalId(
        state.unknownLineItems,
        action.payload
      )
    },
    /**
     * Remove the account balance payment from the sale.
     */
    removePayAccountBalance(state) {
      state.payAccountBalance = null
    },
    /**
     * Update the amount of the account balance payment.
     */
    updatePayAccountBalanceAmount(
      state,
      action: PayloadAction<string | number>
    ) {
      state.payAccountBalance = {
        ...state.payAccountBalance,
        amount: action.payload,
      }
    },
    /**
     * Set the synthetic transaction ID used as the LSPay correlation key
     * in the sale-less ABP flow. Stored on payAccountBalance so it
     * travels with the rest of the ABP state through Redux and IndexedDB.
     */
    setPayAccountBalanceTransactionId(state, action: PayloadAction<string>) {
      state.payAccountBalance = {
        ...(state.payAccountBalance ?? {}),
        amount: state.payAccountBalance?.amount ?? 0,
        abpTransactionId: action.payload,
      }
    },
    /**
     * Persist the in-transit ACH amount for the sale-less ABP Pay screen cap.
     */
    setPayAccountBalanceInTransitAmount(state, action: PayloadAction<number>) {
      state.payAccountBalance = {
        ...(state.payAccountBalance ?? {}),
        amount: state.payAccountBalance?.amount ?? 0,
        inTransitAmount: action.payload,
      }
    },
    /**
     * Update the account balance payment with the details of the given payment.
     *
     * Uses cumulative total of all payments to determine if the ABP amount is
     * covered. This handles split payment scenarios like cash rounding where
     * multiple smaller payments together cover the ABP.
     *
     * Note: This reducer is called after addPayment, so the new payment is
     * already in state.payments when this runs.
     */
    updatePayAccountBalanceWithPayment(state, action: PayloadAction<Payment>) {
      const { payAccountBalance, payments } = state
      const isABPAddedButNotPaid =
        payAccountBalance &&
        Boolean(payAccountBalance.amount && !payAccountBalance.paymentId)
      const payment = action.payload
      if (isABPAddedButNotPaid) {
        // Calculate cumulative total of payments not retrieved from the API.
        const cumulativeTotal = payments
          .filter(p => !p.retrieved)
          .reduce((sum, p) => sum.add(p.amount), vn(0))

        if (cumulativeTotal.gte(payAccountBalance.amount)) {
          payAccountBalance.paymentId = payment.localId
          payAccountBalance.paymentTypeId = payment.paymentType.id
        }
      }
    },
    /**
     * Update the status of the sale.
     */
    updateSaleStatus(state, action: PayloadAction<SaleStatus | undefined>) {
      state.status = action.payload
      state.statusUpdatedAt = moment().format()
    },
    /**
     * Resets the fulfilment details and the sale-level fulfilment attributes.
     *
     * This does not reset the sale status; both callers dispatch `updateSaleStatus`
     * separately and conditionally.
     *
     * The fulfilment attributes are derived from the line items, so they must be cleared
     * whenever the last line item carrying a fulfilment type is removed or unset. Leaving
     * them in place makes the sales API retain the sale's pickup/delivery state, because it
     * treats the submitted attribute list as authoritative.
     */
    removeFulfilmentState(state) {
      state.fulfilmentDetails = [
        {
          type: FulfilmentType.UNSPECIFIED,
          billingAddress: undefined,
          shippingAddress: undefined,
          note: '',
        } as FulfilmentDetail,
      ]
      state.fulfilmentOutletNamesById = {}
      state.attributes = state.attributes.filter(
        attribute => !FULFILMENT_SALE_ATTRIBUTES.includes(attribute)
      )
    },
    /**
     * Add tip to adjustments
     */
    addTip(state, action: PayloadAction<number>) {
      if (action.payload) {
        state.adjustments.push({
          type: TIP_ADJUSTMENT.type,
          name: TIP_ADJUSTMENT.name,
          total: action.payload,
        })
      }
    },
    /**
     * Add surcharge to adjustments
     */
    addSurcharge(state, action: PayloadAction<SurchargeTenderProperties>) {
      if (!action.payload) return
      const { amount, rule } = action.payload

      if (!amount || !rule) return
      state.adjustments.push({
        type: SURCHARGE_ADJUSTMENT.type,
        name: rule,
        total: amount,
      })
    },
    /**
     * Update customer account balances
     */
    updateCustomerBalances(
      state,
      action: PayloadAction<Partial<CustomerBalances>>
    ) {
      if (state.customerBalances) {
        state.customerBalances = {
          ...state.customerBalances,
          ...action.payload,
        }
      }
    },
    /**
     * set customerBalances on the sale
     */
    setCustomerBalances(state, action: PayloadAction<CustomerBalances | null>) {
      state.customerBalances = action.payload
    },
    /**
     * Removes the customer, customerBalances, and payAccountBalance.
     */
    removeCustomer(state) {
      state.customer = null
      state.customerBalances = null
      state.payAccountBalance = null
    },
    /**
     * Sets the sync status to the provided value.
     */
    setSyncStatus(state, action: PayloadAction<SaleSyncStatus>) {
      state.syncStatus = action.payload
    },
    setQuoteId(state, action: PayloadAction<string>) {
      state.quoteId = action.payload
    },
    /**
     * Update sale totals
     */
    setTotals(state, action: PayloadAction<Totals>) {
      state.totals = { ...action.payload }
    },
    /**
     * Update sale finals
     */
    setFinals(state, action: PayloadAction<Finals>) {
      state.finals = { ...action.payload }
    },
    /**
     * Update or add a customField into the customFields array
     */
    setCustomField(state, action: PayloadAction<CustomFieldValue>) {
      const { definitionId, type, value, name } = action.payload
      const item = state.customFields.find(entry => entry.name === name)
      if (item) {
        item.definitionId = definitionId
        item.type = type
        item.value = value
      } else {
        state.customFields.push(action.payload)
      }
    },

    /**
     * Adds a line item to the sale if doesnt exist already
     */
    addLineItem(state, action: PayloadAction<LineItem>) {
      lineItemsAdapter.addOne(state.lineItems, action.payload)
    },

    /**
     * Updates the line item in the sale. It will insert the fields of the line
     * item into the sale if it doesn't exist.
     * @todo This can potentially accept a partial line item as part of the
     * proxy cleanup
     */
    updateLineItem(state, action: PayloadAction<LineItem>) {
      lineItemsAdapter.upsertOne(state.lineItems, action.payload)
    },

    updateLineItems(state, action: PayloadAction<LineItem[]>) {
      lineItemsAdapter.upsertMany(state.lineItems, action.payload)
    },

    setFulfilmentDetails(state, action: PayloadAction<FulfilmentDetail[]>) {
      state.fulfilmentDetails = action.payload
    },

    setFulfilmentOutletNamesById(
      state,
      action: PayloadAction<FulfilmentOutletNamesById>
    ) {
      state.fulfilmentOutletNamesById = action.payload
    },

    /**
     * Updates the sale fulfilment details with the given fulfilment detail.
     * Also updates the fulfilment type of all line items in the sale that are not gift cards.
     */
    setSaleFulfilmentDetail(state, action: PayloadAction<FulfilmentDetail>) {
      state.fulfilmentDetails = [action.payload]

      const lineItems = lineItemsAdapter
        .getSelectors()
        .selectAll(state.lineItems)
        .filter(lineItem => !!lineItem.localId && !isGiftCard(lineItem.product))

      lineItemsAdapter.updateMany(
        state.lineItems,
        lineItems.map(lineItem => ({
          id: lineItem.localId,
          changes: {
            fulfilmentType: action.payload.type,
          },
        }))
      )
    },

    /**
     * Set the customer on the sale.
     */
    attachCustomerToSale(state, action: PayloadAction<ApiCustomer>) {
      state.customer = action.payload
    },

    /**
     * Replaces the existing line items with the given line items.
     */
    setLineItems(state, action: PayloadAction<LineItem[]>) {
      lineItemsAdapter.setAll(state.lineItems, action.payload)
    },

    /**
     * Removes the given line item from the sale.
     */
    removeLineItem(state, action: PayloadAction<LineItem>) {
      lineItemsAdapter.removeOne(state.lineItems, action.payload.localId)
    },
    setReadyToComplete(state, action: PayloadAction<boolean>) {
      state.readyToComplete = action.payload
    },

    /**
     * Update the service fields of the sale
     * Right now only serviceAgenda and serviceItem are supported. Add more as
     * needed
     */
    upsertServiceFields(
      state,
      action: PayloadAction<
        Pick<ServiceFields, 'serviceAgenda' | 'serviceItem'>
      >
    ) {
      const { serviceAgenda, serviceItem } = action.payload

      state.serviceFields = {
        ...state.serviceFields,
        serviceAgenda,
        serviceItem,
        serviceAction:
          state.serviceFields.serviceAction || ServiceActions.CREATE_JOB,
      }
    },
    localIdGenerated(
      state,
      action: PayloadAction<{
        localId: string
        syncAttempts: number
        syncStatus: SaleSyncStatus
        receiptNumberDetails?: string | ReceiptNumberDetails
      }>
    ) {
      if (state.localId !== action.payload.localId) {
        state.localId = action.payload.localId
      }
      if (state.syncAttempts !== action.payload.syncAttempts) {
        state.syncAttempts = action.payload.syncAttempts
      }
      if (state.syncStatus !== action.payload.syncStatus) {
        state.syncStatus = action.payload.syncStatus
      }
      if (
        !isEqual(
          state.receiptNumberDetails,
          action.payload.receiptNumberDetails
        )
      ) {
        state.receiptNumberDetails = action.payload.receiptNumberDetails
      }
    },
    saveComplete(
      state,
      action: PayloadAction<{
        syncStatus?: SaleSyncStatus
        receiptNumberDetails?: string | ReceiptNumberDetails
      }>
    ) {
      if (action.payload.syncStatus) {
        state.syncStatus = action.payload.syncStatus
      }
      if (action.payload.receiptNumberDetails) {
        state.receiptNumberDetails = action.payload.receiptNumberDetails
      }
    },
    setPromoCode(state, stateAction: PayloadAction<string>) {
      state.promoCode = stateAction.payload
    },
    setPromoCodeStatus(state, stateAction: PayloadAction<string | null>) {
      state.promoCodeStatus = stateAction.payload
    },
    setPromoCodePromotionId(state, stateAction: PayloadAction<string | null>) {
      state.promoCodePromotionId = stateAction.payload
    },
    setPotentialPromotionId(state, stateAction: PayloadAction<string | null>) {
      state.potentialPromotionId = stateAction.payload
    },
    clearPotentialPromotionId(state) {
      state.potentialPromotionId = null
    },
    setChange(state, stateAction: PayloadAction<Payment | null>) {
      state.change = stateAction.payload
    },

    /**
     * When sale status transitions to and from PARKED, or to QUOTE
     * We need to remove some sale fields so they can be regenerated
     */
    parkedOrQuoteTransitioned(state) {
      // Don't clear user for parked returns, to record returns under
      // the user who made the oringinal sale (see ReturnSaleService.php)
      if (!isReturn(state)) {
        state.user = null
      }
      state.saleDate = null
      state.registerId = null
    },

    saleReadyToBeCompleted(state) {
      state.readyToComplete = true
    },

    /**
     * Update the customer balance to reflect any account balance payments made during the sale.
     * This is done by adding the payment amount to the current (negative) customer balance.
     */
    accountBalancePaymentAdded(state) {
      const newAccount = add(
        state.customerBalances?.account ?? 0,
        state.payAccountBalance?.amount ?? 0
      )

      const newAvailableAccountLimitBalance = state.customer?.on_account_limit
        ? add(
            state.customerBalances?.availableOnAccountLimitBalance ?? 0,
            state.payAccountBalance?.amount ?? 0
          )
        : 0

      if (state.customerBalances) {
        state.customerBalances = {
          ...state.customerBalances,
          account: newAccount,
          availableOnAccountLimitBalance: newAvailableAccountLimitBalance,
        }
      }
    },

    displayStatusChanged(state) {
      const lineItems = lineItemsAdapter
        .getSelectors()
        .selectAll(state.lineItems)
      state.displayStatus = determineDisplayStatusRedux(
        state,
        lineItems
      ) as SaleStatus
    },

    dateChanged(state) {
      state.saleDate = moment().format()
    },

    registerChanged(state, action: PayloadAction<string | undefined>) {
      state.registerId = action.payload
    },

    userChanged(state, action: PayloadAction<User>) {
      state.user = action.payload
    },

    hideAttachCustomerTooltip(state, action: PayloadAction<boolean>) {
      state.hideAttachCustomerTooltip = action.payload
    },

    receiptNumberDetailsChanged(
      state,
      action: PayloadAction<ReceiptNumberDetails | undefined>
    ) {
      state.receiptNumberDetails = action.payload
    },

    /**
     * reducer to update sale display discount
     */
    updateDisplayDiscount(
      state,
      action: PayloadAction<
        Pick<ReduxCurrentSale, 'displayDiscount' | 'discountIsPercentage'>
      >
    ) {
      state.displayDiscount = action.payload.displayDiscount
      state.discountIsPercentage = action.payload.discountIsPercentage
    },

    /**
     * reducer to update sale discount
     */
    updateSaleDiscount(
      state,
      action: PayloadAction<Pick<ReduxCurrentSale, 'discount'>>
    ) {
      state.discount = action.payload.discount
    },

    /**
     * Update the customFields array with the given customFields.
     * NOTES: This will replace the existing customFields array with the given customFields. But it doens't handle line item custom fields.
     */
    customFieldsSet(
      state,
      action: PayloadAction<{
        customFields: CustomFieldValue[]
        hasUpdatedCustomFieldsOnConfirmation?: boolean
      }>
    ) {
      state.customFields = action.payload.customFields
      state.hasUpdatedCustomFieldsOnConfirmation =
        action.payload.hasUpdatedCustomFieldsOnConfirmation
    },
    saveSaleEarlyEnabled(state) {
      state.isSubmittedSaleEarly = true
    },
    updateReceiptValues(
      state,
      action: PayloadAction<{ receiptEmailAddress: string; receiptID: string }>
    ) {
      state.receiptEmailAddress = action.payload.receiptEmailAddress
      state.receiptID = action.payload.receiptID
    },
    finaliseSaleSyncStatus(state) {
      return {
        ...state,
        // Clear the local readyToComplete property for layby and on account sales
        readyToComplete: !isConfirmed(state),
        syncStatus: SaleSyncStatus.UPLOAD,
      }
    },
    updatePickable(state, action: PayloadAction<boolean>) {
      state.pickable = action.payload
    },

    storeCreditBalanceChanged(state, action: PayloadAction<number>) {
      if (state.customerBalances) {
        state.customerBalances.storeCredit.balance = action.payload
      }
    },

    setPotentialPromotionIdAndLineItems(
      state,
      action: PayloadAction<{
        potentialPromotionId: string | null | undefined
        lineItems: LineItem[]
      }>
    ) {
      state.potentialPromotionId = action.payload.potentialPromotionId
      lineItemsAdapter.setAll(state.lineItems, action.payload.lineItems)
    },

    setPromotionAndLineItems(
      state,
      action: PayloadAction<{
        promoCodeStatus: string | null
        promoCode: string
        promoCodePromotionId: string | null
        potentialPromotionId: string | null | undefined
        lineItems: LineItem[]
      }>
    ) {
      state.promoCodeStatus = action.payload.promoCodeStatus
      state.promoCode = action.payload.promoCode
      state.promoCodePromotionId = action.payload.promoCodePromotionId
      state.potentialPromotionId = action.payload.potentialPromotionId
      lineItemsAdapter.setAll(state.lineItems, action.payload.lineItems)
    },

    setUserRemovedSaleServiceFee(state, action: PayloadAction<boolean>) {
      state.userRemovedSaleServiceFee = action.payload
    },

    /**
     * Sets the LSPay cash discounting rate (percentage) that should be
     * applied to the current sale. Pass `0` to remove it.
     */
    setLspCashDiscountPercentage(state, action: PayloadAction<number>) {
      state.lspCashDiscountPercentage = action.payload || 0
    },

    /**
     * Sets the `saleSubmittedEarly` flag to true.
     */
    saleSubmittedEarly(state) {
      state.isSubmittedSaleEarly = true
    },
    /**
     * Sets the `completedByUser` field to the given source
     */
    setSaleCompletedBy(state, action: PayloadAction<string>) {
      state.completedByUser = { source: action.payload }
    },
    /**
     * Sets the `hasAddedCustomerOnConfirmation` flag to true.
     */
    customerAddedOnConfirmation(state) {
      state.hasAddedCustomerOnConfirmation = true
    },

    /**
     * Sale closed, currently only generates a loyalty code if it's not set
     */
    saleClosed(state) {
      if (state.shortCode === undefined) {
        state.shortCode = generateSaleLoyaltyClaimCode()
      }
    },

    /**
     * Generate and set a random short code for the sale. Used for loyalty claim * urls.
     */
    generateLoyaltyClaimCode(state) {
      const shortCode = generateSaleLoyaltyClaimCode()
      state.shortCode = shortCode
    },

    /**
     * Update the calculable fields of the sale and line items.
     */
    updateSaleCalculableFields(
      state,
      action: PayloadAction<UpdateSaleCalculableFieldsPayload>
    ) {
      const {
        lineItems,
        discount,
        totals,
        finals,
        displayStatus,
      } = action.payload

      const lineItemsForMutation: Array<Update<LineItem>> = lineItems.map(
        lineItem => {
          return {
            id: lineItem.localId,
            changes: {
              totals: lineItem.totals,
              taxInfo: lineItem.taxInfo,
              taxComponents: lineItem.taxComponents,
              customerTaxApplied: lineItem.customerTaxApplied,
            },
          }
        }
      )

      lineItemsAdapter.updateMany(state.lineItems, lineItemsForMutation)
      state.discount = discount
      state.totals = totals
      state.finals = finals
      state.displayStatus = displayStatus
    },

    /**
     * Update line item tax information for a line item.
     * Note: This action is no-op if the line item does not exist.
     */
    updateLineItemTax(
      state,
      {
        payload: { lineItemLocalID, adjusted, customerTaxApplied, taxInfo },
      }: PayloadAction<UpdateLineItemTaxPayload>
    ) {
      // No need to check if the record exist or not because the adapter already does that https://github.com/reduxjs/redux-toolkit/blob/f0088d3/packages/toolkit/src/entities/unsorted_state_adapter.ts#L140-L142
      lineItemsAdapter.updateOne(state.lineItems, {
        id: lineItemLocalID,
        changes: {
          adjusted,
          customerTaxApplied,
          taxInfo,
        },
      })
    },

    updateInitialPaidBalance(state, action: PayloadAction<number>) {
      state.paidBalance = action.payload
    },

    /**
     * Sets the missing sale id to the local id
     */
    writeMissingSaleId(state) {
      state.id = state.localId
    },

    updateCalculatingStateOfAllLineItems(
      state,
      action: PayloadAction<boolean>
    ) {
      const lineItemsForMutation: Array<Update<LineItem>> = lineItemsAdapter
        .getSelectors()
        .selectAll(state.lineItems)
        .map(lineItem => {
          return {
            id: lineItem.localId,
            changes: {
              calculating: action.payload,
            },
          }
        })

      lineItemsAdapter.updateMany(state.lineItems, lineItemsForMutation)
    },

    removeEcomCustomCharge(state, action: PayloadAction<string>) {
      if (state.ecomCustomCharges) {
        const updatedCharges = state.ecomCustomCharges.charges?.filter(
          charge => charge.sourceId !== action.payload
        )
        const { total, totalIncl, totalTax } = updatedCharges.reduce(
          (acc, charge) => {
            acc.total += charge.total
            acc.totalIncl += charge.totalIncl
            acc.totalTax += charge.totalTax
            return acc
          },
          { total: 0, totalIncl: 0, totalTax: 0 }
        )

        state.ecomCustomCharges = {
          total,
          totalIncl,
          totalTax,
          charges: updatedCharges,
        }
        state.hasEcomCustomCharges = true
      }
    },

    /**
     * Add a paymentrequest to sale attributes
     */
    addPaymentRequestSaleAttribute(state) {
      const hasPaymentRequest = state.attributes.includes(
        RegisterSaleAttribute.PAYMENTREQUEST
      )
      if (!hasPaymentRequest) {
        state.attributes.push(RegisterSaleAttribute.PAYMENTREQUEST)
      }
    },

    /**
     * Set auto-tax calculation loading state
     */
    setAutoTaxCalculating(state, action: PayloadAction<boolean>) {
      state.autoTaxCalculating = action.payload
    },

    /**
     * Set the last calculated auto-tax input key
     */
    setAutoTaxLastCalculatedKey(state, action: PayloadAction<string>) {
      state.autoTaxLastCalculatedKey = action.payload
    },

    /**
     * Store original finals before auto-tax replaces them
     */
    setPreAutoTaxFinals(state, action: PayloadAction<Finals | null>) {
      state.preAutoTaxFinals = action.payload
    },

    /**
     * Store or clear original tax info for line items before auto-tax replaces them
     */
    setPreAutoTaxLineItems(
      state,
      action: PayloadAction<
        Record<
          string,
          {
            tax: string
            baseTax: string
            taxInfo?: ApiTax
            taxComponents: LineItemTaxComponent[]
          }
        >
      >
    ) {
      state.preAutoTaxLineItems = action.payload
    },

    /**
     * Set the tax calculation error
     */
    setCalculateTaxError(
      state,
      action: PayloadAction<CalculateTaxErrorType | undefined>
    ) {
      state.calculateTaxError = action.payload
    },

    /**
     * Set the line item IDs that have inactive/outdated tax categories
     */
    setAutoTaxInactiveCategoryLineItemIds(
      state,
      action: PayloadAction<string[] | undefined>
    ) {
      state.autoTaxInactiveCategoryLineItemIds = action.payload
    },

    /**
     * Set the flag indicating whether payment requests were auto-cancelled
     */
    setPaymentRequestsAutoCancelled: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.paymentRequestsAutoCancelled = action.payload
    },
  },
})

export const { reducer } = currentSaleSlice
export const {
  addOrRemovePromoCode,
  attachCustomerToSale,
  clearPotentialPromotionId,
  newCurrentSale,
  setCurrentSale,
  setSaleNote,
  setFulfillmentOutletID,
  addPayment,
  addUnknownLineItem,
  removeUnknownLineItem,
  removePayAccountBalance,
  updatePayAccountBalanceAmount,
  setPayAccountBalanceTransactionId,
  setPayAccountBalanceInTransitAmount,
  updatePayAccountBalanceWithPayment,
  updateSaleStatus,
  addTip,
  addSurcharge,
  updateCustomerBalances,
  removeCustomer,
  setSyncStatus,
  setQuoteId,
  setTotals,
  setFinals,
  setCustomField,
  addLineItem,
  updateLineItem,
  updateLineItems,
  setLineItems,
  removeLineItem,
  setFulfilmentDetails,
  setFulfilmentOutletNamesById,
  setSaleFulfilmentDetail,
  upsertServiceFields,
  localIdGenerated,
  saveComplete,
  setReadyToComplete,
  setHasUpdateNoteOnConfirmation,
  setPromoCode,
  setPromoCodeStatus,
  setPromoCodePromotionId,
  setPotentialPromotionId,
  setChange,
  parkedOrQuoteTransitioned,
  saleReadyToBeCompleted,
  accountBalancePaymentAdded,
  displayStatusChanged,
  dateChanged,
  registerChanged,
  userChanged,
  receiptNumberDetailsChanged,
  hideAttachCustomerTooltip,
  updateSaleDiscount,
  updateDisplayDiscount,
  customFieldsSet,
  saveSaleEarlyEnabled,
  updateReceiptValues,
  finaliseSaleSyncStatus,
  updatePickable,
  storeCreditBalanceChanged,
  setCustomerBalances,
  setPotentialPromotionIdAndLineItems,
  setPromotionAndLineItems,
  setUserRemovedSaleServiceFee,
  setLspCashDiscountPercentage,
  saleSubmittedEarly,
  setSaleCompletedBy,
  customerAddedOnConfirmation,
  saleClosed,
  generateLoyaltyClaimCode,
  updateSaleCalculableFields,
  updateLineItemTax,
  updateInitialPaidBalance,
  writeMissingSaleId,
  updateCalculatingStateOfAllLineItems,
  removeFulfilmentState,
  removeEcomCustomCharge,
  addPaymentRequestSaleAttribute,
  setAutoTaxCalculating,
  setAutoTaxLastCalculatedKey,
  setPreAutoTaxFinals,
  setPreAutoTaxLineItems,
  setCalculateTaxError,
  setAutoTaxInactiveCategoryLineItemIds,
  setPaymentRequestsAutoCancelled,
} = currentSaleSlice.actions

export const selectCurrentSale = (state: RootState) => state.currentSale
export const selectLspCashDiscountPercentage = (state: RootState) =>
  state.currentSale.lspCashDiscountPercentage ?? 0
export const selectCurrentSaleAsSale = (state: RootState): Sale => {
  const currentSale = selectCurrentSale(state)
  const currentSaleForModel = { ...currentSale }
  delete (currentSaleForModel as Partial<ReduxCurrentSale>)
    .fulfilmentOutletNamesById
  const sale = new Sale({
    ...currentSaleForModel,
    lineItems: selectLineItems(state),
  })

  return sale
}
export const selectSaleStatus = (state: RootState) => state.currentSale.status
export const selectSaleDisplayStatus = (state: RootState) =>
  state.currentSale.displayStatus
export const selectIsSaleUnfulfilled = createSelector(
  [selectSaleStatus],
  isUnfulfilled
)
export const selectSaleId = (state: RootState) => state.currentSale.id
export const selectQuoteId = (state: RootState) => state.currentSale.quoteId
export const selectSaleLocalId = (state: RootState) => state.currentSale.localId
export const selectSaleNote = (state: RootState) => state.currentSale.note
export const selectSaleCustomer = (state: RootState) =>
  state.currentSale.customer
export const selectUnknownLineItems = (state: RootState) =>
  state.currentSale.unknownLineItems
export const selectSaleUser = (state: RootState) => state.currentSale.user
export const selectSaleFulfilmentDetail = (state: RootState) =>
  createSelector(
    [selectFulfilmentDetails],
    fulfilmentDetails => fulfilmentDetails[0]
  )(state)
export const selectFulfilmentDetails = (state: RootState) =>
  state.currentSale.fulfilmentDetails
export const selectFulfilmentOutletNamesById = (state: RootState) =>
  state.currentSale.fulfilmentOutletNamesById ?? {}
export const selectSaleAttributes = (state: RootState) =>
  new Set(state.currentSale.attributes)
export const selectSaleAttributesHasFulfilment = (state: RootState) => {
  const saleAttributes = selectSaleAttributes(state)
  return [
    SaleAttributes.FULFILMENT_DELIVERY,
    SaleAttributes.FULFILMENT_PICKUP,
  ].some(attribute => saleAttributes.has(attribute))
}
export const selectIsSubmittedSaleEarly = (state: RootState) =>
  state.currentSale.isSubmittedSaleEarly
export const selectSalePromoCode = (state: RootState) =>
  state.currentSale.promoCode
export const selectSalePromoCodeStatus = (state: RootState) =>
  state.currentSale.promoCodeStatus
export const selectSalePromoCodePromotionId = (state: RootState) =>
  state.currentSale.promoCodePromotionId
export const selectSaleTotals = (state: RootState) => state.currentSale.totals
export const selectSaleTotalsServiceFee = (state: RootState) =>
  state.currentSale.totals.saleServiceFee
export const selectSaleDisplayDiscount = (state: RootState) =>
  state.currentSale.displayDiscount
export const selectSaleDiscountIsPercentage = (state: RootState) =>
  state.currentSale.discountIsPercentage
export const selectLineItem = (localId: string) => (state: RootState) =>
  lineItemsAdapter
    .getSelectors()
    .selectById(state.currentSale.lineItems, localId)
const selectLineItemsState = (state: RootState) => state.currentSale.lineItems
export const selectLineItems = createSelector(
  [selectLineItemsState],
  lineItemsState => lineItemsAdapter.getSelectors().selectAll(lineItemsState)
)
export const selectLineItemsTotalQuantity = createSelector(
  [selectLineItems],
  lineItems =>
    lineItems
      .filter(lineItem => !isGiftCard(lineItem.product))
      .reduce((acc, lineItem) => acc + Number(lineItem.quantity), 0)
)
// Line items that actually require fulfilment (delivery/pickup) — excludes
// take-now items (fulfilmentType UNSPECIFIED) and gift cards. Used by the
// "Fulfillment Note" UBE so productCount/totalQuantity reflect only the
// fulfilment portion of the sale, not the whole basket.
export const selectFulfilmentLineItems = createSelector(
  [selectLineItems],
  lineItems =>
    lineItems.filter(
      lineItem =>
        !isGiftCard(lineItem.product) &&
        isDefinedFulfilmentType(lineItem.fulfilmentType)
    )
)
export const selectFulfilmentLineItemsProductCount = createSelector(
  [selectFulfilmentLineItems],
  lineItems => lineItems.length
)
export const selectFulfilmentLineItemsTotalQuantity = createSelector(
  [selectFulfilmentLineItems],
  lineItems =>
    lineItems.reduce((acc, lineItem) => acc + Number(lineItem.quantity), 0)
)
export const selectLineItemFulfilmentType = (localId: string) => (
  state: RootState
) =>
  lineItemsAdapter
    .getSelectors()
    .selectById(state.currentSale.lineItems, localId)?.fulfilmentType
export const selectHasCompletedFulfilments = createSelector(
  [selectLineItems],
  lineItems => lineItems.some(lineItem => !!lineItem.fulfilmentCompleted)
)
export const selectNoteHasCreditCard = (state: RootState) =>
  detectCreditCard(state.currentSale.note)
export const selectNoteHasTooManyCharacters = (state: RootState) => {
  const noteMaxChars = 2000
  return state.currentSale.note.length > noteMaxChars
}
export const selectNoteIsInvalid = (state: RootState) =>
  selectNoteHasCreditCard(state) || selectNoteHasTooManyCharacters(state)

export const selectFulfilmentNoteHasCreditCard = (state: RootState) =>
  detectCreditCard(state.currentSale.fulfilmentDetails[0]?.note)
export const selectFulfilmentNoteHasTooManyCharacters = (state: RootState) =>
  // since all fulfilment detail note are the same, we can just check the first one
  isNoteTooLong(state.currentSale.fulfilmentDetails[0]?.note)
export const selectFulfilmentNoteIsInvalid = (state: RootState) =>
  selectFulfilmentNoteHasCreditCard(state) ||
  selectFulfilmentNoteHasTooManyCharacters(state)

// return a map of the sale product entities with inventory tracking enabled
export const selectSaleProductIdWithTrackedInventory = createSelector(
  [selectLineItems],
  lineItems => {
    const products: Record<string, ApiProduct> = {}
    for (const lineItem of lineItems) {
      if (lineItem.product.has_inventory) {
        products[lineItem.product.id] = lineItem.product
      }
    }
    return products
  }
)

export const selectSaleHasLineItemFulfilment = createSelector(
  [selectLineItems],
  hasLineItemFulfilment
)
export const selectPotentialPromotionId = (state: RootState) =>
  state.currentSale.potentialPromotionId

export const selectPaymentHeading = createSelector(
  [selectCurrentSaleAsSale],
  sale => {
    if (!isReturn(sale) && !saleIsExchange(sale)) {
      if (!isCompleted(sale.status)) {
        return 'Amount to Pay'
      } else {
        return 'Payment'
      }
    } else {
      if (saleIsExchange(sale)) {
        return 'Exchange'
      } else {
        return 'Return'
      }
    }
  }
)
export const selectServiceFields = (state: RootState) =>
  state.currentSale.serviceFields
export const selectCustomFields = (state: RootState) =>
  state.currentSale.customFields
export const selectIsSavedService = (state: RootState) =>
  state.currentSale.isSavedService
export const selectSaleFinals = (state: RootState) => state.currentSale.finals
export const selectSaleFinalTaxDisplay = (state: RootState) =>
  state.currentSale.finals.taxDisplayProperties

export const selectSalePayments = (state: RootState) =>
  state.currentSale.payments

export const selectOutstandingBalance = createSelector(
  [
    selectSaleFinals,
    selectSalePayments,
    (state: RootState) => state.currentSale.payAccountBalance,
  ],
  (finals, payments, payAccountBalance) =>
    getOutstandingBalance({
      finals,
      payments,
      payAccountBalance,
    })
)

export const selectPromoCodeAndStatus = createSelector(
  [selectSalePromoCode, selectSalePromoCodeStatus],
  (promoCode, promoCodeStatus) => {
    return {
      promoCode,
      promoCodeStatus,
    }
  }
)

export const selectPaidBalance = (state: RootState) =>
  state.currentSale.paidBalance

export const selectSaleSummaryFields = createSelector(
  (state: RootState) => state.currentSale.finals,
  (state: RootState) => state.currentSale.discount,
  (state: RootState) => state.currentSale.status,
  (state: RootState) => state.currentSale.totals,
  (state: RootState) => state.currentSale.payAccountBalance,
  (state: RootState) => state.currentSale.change,
  (state: RootState) => state.currentSale.payments,

  (finals, discount, status, totals, payAccountBalance, change, payments) => ({
    finals,
    discount,
    status,
    totals,
    payAccountBalance,
    change,
    payments,
  })
)

export const selectPromotionSummary = createSelector(
  [selectLineItems],
  getPromotionSummary
)
export const selectPayAccountBalance = (state: RootState) =>
  state.currentSale.payAccountBalance

export const selectChange = (state: RootState) => state.currentSale.change
export const selectToPay = createSelector(
  [selectSalePayments, selectSaleFinals, selectPayAccountBalance, selectChange],
  (payments, finals, payAccountBalance, change) => ({
    payments,
    finals,
    payAccountBalance,
    change,
  })
)

export const selectEcomCustomCharges = (state: RootState) =>
  state.currentSale.ecomCustomCharges

export const selectCustomerBalances = (state: RootState) =>
  state.currentSale.customerBalances

export const selectHideAttachCustomerTooltip = (state: RootState) =>
  state.currentSale.hideAttachCustomerTooltip

export const selectOriginalSalePayments = (state: RootState) =>
  state.currentSale.originalSalePayments

export const selectSaleSyncStatus = createSelector(
  [(state: RootState) => state.currentSale.syncStatus],
  syncStatus => syncStatus
)

export const selectDisplayStatus = createSelector(
  [(state: RootState) => state.currentSale.displayStatus],
  displayStatus => displayStatus
)

export const selectCustomer = createSelector(
  [(state: RootState) => state.currentSale.customer],
  customer => customer
)

/**
 * Calculates the total amount of partial payments made with the specified payment type
 * Excludes any 'retrieved'payments unless the "includeRetrievedPayments" option is passed.
 */
export const selectTotalPartialPayments = (
  paymentTypeId: number,
  includeRetrievedPayments: boolean = false
) => {
  return (state: RootState) => {
    return state.currentSale.payments.reduce((total, payment) => {
      if (
        payment.paymentType.type_id === paymentTypeId &&
        (includeRetrievedPayments || !payment.retrieved)
      ) {
        return add(total, payment.amount)
      } else {
        return total
      }
    }, 0)
  }
}

/**
 * Determines whether at least one payment of the specified payment type exists on the sale.
 * Unlike selectTotalPartialPayments, this is presence-based rather than net-total-based:
 * a payment type with both positive and negative entries (e.g. an On Account payment followed
 * by a refund) that net to zero or less still counts as present here.
 * Excludes any 'retrieved' payments unless the "includeRetrievedPayments" option is passed.
 */
export const selectHasPaymentOfType = (
  paymentTypeId: number,
  includeRetrievedPayments: boolean = false
) => {
  return (state: RootState) => {
    return state.currentSale.payments.some(
      payment =>
        payment.paymentType.type_id === paymentTypeId &&
        (includeRetrievedPayments || !payment.retrieved)
    )
  }
}
export const selectHasUpdatedNoteOnConfirmation = (state: RootState) =>
  state.currentSale.hasUpdatedNoteOnConfirmation

export const selectSaleAttributesHasPaymentRequest = (state: RootState) => {
  const saleAttributes = selectSaleAttributes(state)
  return [SaleAttributes.PAYMENT_REQUEST].some(attribute =>
    saleAttributes.has(attribute)
  )
}

export const selectIsEcomOrder = (state: RootState) =>
  !!(
    state.currentSale.id &&
    state.currentSale.sourceId &&
    state.currentSale.sourceId !== ''
  )

export const selectIsParkedSale = (state: RootState) =>
  !!(state.currentSale.id && !state.currentSale.isSavedUnfulfilled)

export const selectHasPicklist = (state: RootState) =>
  state.currentSale.picklists && state.currentSale.picklists.length > 0

export const selectAutoTaxCalculating = (state: RootState) =>
  state.currentSale.autoTaxCalculating

export const selectAutoTaxLastCalculatedKey = (state: RootState) =>
  state.currentSale.autoTaxLastCalculatedKey

export const selectPreAutoTaxFinals = (state: RootState) =>
  state.currentSale.preAutoTaxFinals

export const selectPreAutoTaxLineItems = (state: RootState) =>
  state.currentSale.preAutoTaxLineItems

export const selectCalculateTaxError = (state: RootState) =>
  state.currentSale.calculateTaxError

export const selectAutoTaxInactiveCategoryLineItemIds = (state: RootState) =>
  state.currentSale.autoTaxInactiveCategoryLineItemIds

export const selectPaymentRequestsAutoCancelled = (state: RootState) =>
  state.currentSale.paymentRequestsAutoCancelled
